import { useEffect, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';

export interface MultipleTabCheckResult {
  isActiveTab: boolean;
  showModal: boolean;
}

const ACTIVE_TAB_ID_KEY = 'savichat_active_tab';

interface CookieType {
  [ACTIVE_TAB_ID_KEY]: string;
}

const useMultipleTabCheck = (): MultipleTabCheckResult => {
  const [isActiveTab, setIsActiveTab] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // generate a unique tab ID when the hook initializes
  const tabIdRef = useRef<string>(`${Date.now()}_${Math.random()}`);
  const isActiveTabRef = useRef<boolean>(false);

  const [cookies, setCookie, removeCookie] = useCookies<string, CookieType>([
    ACTIVE_TAB_ID_KEY,
  ]);

  // use session cookies in case of a browser crash.
  // the user won't be locked out because the browser will not retain the cookie.
  const setSessionCookie = (value: string) => {
    setCookie(ACTIVE_TAB_ID_KEY, value, { path: '/' });
    // No 'expires' or 'maxAge' makes it a session cookie
  };

  const getSessionCookie = (key: keyof CookieType) => cookies[key];

  const removeSessionCookie = (key: keyof CookieType) => {
    removeCookie(key, { path: '/' });
  };

  const checkActiveTabStatus = () => {
    // check and update the active tab status
    const activeTabId = getSessionCookie(ACTIVE_TAB_ID_KEY);

    if (!activeTabId) {
      setSessionCookie(tabIdRef.current);
      setIsActiveTab(true);
      isActiveTabRef.current = true;
      setShowModal(false);
    } else if (activeTabId === tabIdRef.current) {
      setIsActiveTab(true);
      isActiveTabRef.current = true;
      setShowModal(false);
    } else {
      setIsActiveTab(false);
      isActiveTabRef.current = false;
      setShowModal(true);
    }
  };

  // clean up on tab closure
  const handleDeleteActiveTabCookie = () => {
    if (isActiveTabRef.current) {
      removeSessionCookie(ACTIVE_TAB_ID_KEY);
    }
  };

  const handleBeforeUnload = () => {
    handleDeleteActiveTabCookie();
  };

  useEffect(() => {
    // initial check on component mount
    checkActiveTabStatus();

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      handleDeleteActiveTabCookie();
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // empty dependency array to run only once on mount

  return { isActiveTab, showModal };
};

export default useMultipleTabCheck;
